import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Grid,
    IconButton,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";

// Calculate 1/4 wave antenna length in inches
const calculateAntennaLength = (frequencyMHz) => {
    if (!frequencyMHz || frequencyMHz <= 0) return "";
    const lengthInches = 2828 / parseFloat(frequencyMHz);
    return `${Math.round(lengthInches)}"`;
};

const getDefaultFrequencies = (magnetType) => {
    console.log("getDefaultFrequencies called with:", magnetType);
    let frequencies = [];

    if (magnetType && magnetType.includes("High Spec")) {
        // High Spec has no default frequencies - user must enter all
        return [];
    } else if (
        magnetType &&
        magnetType.includes("GE") &&
        magnetType.includes("1.5")
    ) {
        frequencies = [
            { frequency: "51.251", unit: "MHz" },
            { frequency: "63.751", unit: "MHz" },
            { frequency: "76.751", unit: "MHz" },
        ];
    } else if (
        magnetType &&
        magnetType.includes("GE") &&
        magnetType.includes("3.0")
    ) {
        frequencies = [
            { frequency: "102.25", unit: "MHz" },
            { frequency: "127.751", unit: "MHz" },
            { frequency: "153.251", unit: "MHz" },
        ];
    } else {
        // All Others (Siemens, Philips, Fujifilm, Other)
        frequencies = [
            { frequency: "64.251", unit: "MHz" },
            { frequency: "101.251", unit: "MHz" },
            { frequency: "128.251", unit: "MHz" },
        ];
    }

    // Calculate antenna length for each frequency
    return frequencies.map((f) => ({
        ...f,
        antennaLength: calculateAntennaLength(f.frequency),
    }));
};

export default function FrequencyReferenceEntry({
    testPoints,
    frequencies,
    magnetType,
    onSave,
    onChange,
}) {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

    const [freqData, setFreqData] = useState(() => {
        if (frequencies && frequencies.length > 0) {
            return frequencies;
        }
        const defaults = getDefaultFrequencies(magnetType);
        return defaults.map((f) => ({
            ...f,
            reference: "",
        }));
    });

    const previousMagnetTypeRef = useRef(magnetType);

    // Update frequencies when prop changes (e.g., loading existing data in edit mode)
    useEffect(() => {
        if (frequencies && frequencies.length > 0) {
            setFreqData(frequencies);
        }
    }, [frequencies]);

    // Update frequencies when magnetType changes
    useEffect(() => {
        console.log(
            "magnetType changed:",
            magnetType,
            "previous:",
            previousMagnetTypeRef.current,
        );

        if (previousMagnetTypeRef.current !== magnetType) {
            const defaults = getDefaultFrequencies(magnetType);
            // Preserve any existing reference values if frequency matches
            setFreqData((currentFreqData) => {
                const newFreqData = defaults.map((defaultFreq) => {
                    const existing = currentFreqData.find(
                        (f) => f.frequency === defaultFreq.frequency,
                    );
                    return {
                        ...defaultFreq,
                        reference: existing ? existing.reference : "",
                    };
                });

                // Notify parent of the change
                if (onChange) {
                    onChange(newFreqData);
                }

                return newFreqData;
            });

            previousMagnetTypeRef.current = magnetType;
        }
    }, [magnetType, onChange]);

    const handleAddFrequency = () => {
        const newData = [
            ...freqData,
            {
                frequency: "",
                unit: "MHz",
                antennaLength: "",
                reference: "",
            },
        ];
        setFreqData(newData);
        if (onChange) {
            onChange(newData);
        }
    };

    const handleRemoveFrequency = (index) => {
        const newData = freqData.filter((_, i) => i !== index);
        setFreqData(newData);
        if (onChange) {
            onChange(newData);
        }
    };

    const handleFrequencyChange = (index, value) => {
        const newData = [...freqData];
        newData[index].frequency = value;
        // Auto-calculate antenna length for 1/4 wave
        newData[index].antennaLength = calculateAntennaLength(value);
        setFreqData(newData);
        if (onChange) {
            onChange(newData);
        }
    };

    const handleReferenceChange = (freqIndex, value) => {
        const newData = [...freqData];
        newData[freqIndex].reference = value;
        setFreqData(newData);
        if (onChange) {
            onChange(newData);
        }
    };

    const handleSave = () => {
        onSave(freqData);
    };

    const isValid = () => {
        return freqData.some((f) => {
            return f.frequency && f.reference !== "";
        });
    };

    return (
        <Box>
            <Box sx={{ p: { xs: 1, sm: 1.5 }, mb: { xs: 1, sm: 1.5 } }}>
                <Box sx={{ mb: { xs: 1, sm: 1.5 } }}>
                    <Typography variant="body1" gutterBottom>
                        Enter reference value for each frequency (taken once per
                        frequency):
                    </Typography>
                    {freqData.length > 0 && freqData[0].antennaLength && (
                        <Box sx={{ mt: 1 }}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                            >
                                Antenna Lengths:
                            </Typography>
                            {freqData.map((f, i) => (
                                <Typography
                                    key={i}
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ pl: 2, fontSize: "0.875rem" }}
                                >
                                    • {f.frequency} MHz = {f.antennaLength}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Box>

                {freqData.map((freq, freqIndex) => (
                    <Box
                        key={freqIndex}
                        sx={{
                            mb: { xs: 1, sm: 1.5 },
                            p: { xs: 0.5, sm: 1 },
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                        }}
                    >
                        <Grid
                            container
                            spacing={{ xs: 1, sm: 2 }}
                            alignItems="center"
                        >
                            <Grid item xs={5} sm={5}>
                                <TextField
                                    label="Frequency (MHz)"
                                    value={freq.frequency}
                                    onChange={(e) =>
                                        handleFrequencyChange(
                                            freqIndex,
                                            e.target.value,
                                        )
                                    }
                                    fullWidth
                                    size="small"
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={5} sm={6}>
                                <TextField
                                    label={
                                        isSmallScreen
                                            ? "Ref.(dBμV)"
                                            : "Reference (dBμV)"
                                    }
                                    value={freq.reference || ""}
                                    onChange={(e) =>
                                        handleReferenceChange(
                                            freqIndex,
                                            e.target.value,
                                        )
                                    }
                                    fullWidth
                                    size="small"
                                    type="number"
                                />
                            </Grid>
                            <Grid
                                item
                                xs={2}
                                sm={1}
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                }}
                            >
                                <IconButton
                                    onClick={() =>
                                        handleRemoveFrequency(freqIndex)
                                    }
                                    color="error"
                                    size="small"
                                >
                                    <ClearIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Box>
                ))}

                {/* Add Frequency Button */}
                <Box
                    onClick={handleAddFrequency}
                    sx={{
                        p: { xs: 1, sm: 1.5 },
                        border: "1px solid",
                        borderColor: "primary.light",
                        borderRadius: 1,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        color: "text.secondary",
                        transition: "all 0.2s",
                        "&:hover": {
                            borderColor: "primary.main",
                            color: "primary.main",
                            bgcolor: "action.hover",
                        },
                    }}
                >
                    <AddIcon fontSize="small" />
                    <Typography variant="body2">Add Frequency</Typography>
                </Box>
            </Box>
        </Box>
    );
}
